/**
 * Lifts Hooks
 *
 * Provides hooks for lift CRUD operations with TanStack Query.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

// Lift types
export type LiftType = '2_post' | '4_post' | 'scissor' | 'pit';
export type MaintenanceStatus = 'operational' | 'needs_maintenance' | 'out_of_service';

export interface Lift {
  id: string;
  name: string;
  identifier: string;
  type: LiftType;
  capacityKg: number;
  lengthCm: number;
  widthCm: number;
  maxHeightCm: number;
  workshopLocation: string | null;
  maintenanceStatus: MaintenanceStatus;
  lastMaintenanceAt: string | null;
  nextMaintenanceAt: string | null;
  notes: string | null;
  isActive: boolean;
  floorPlanX: number | null;
  floorPlanY: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface LiftWithAvailability extends Lift {
  isAvailable: boolean;
  currentBookingId: string | null;
  canAccommodateVehicle?: boolean;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export interface LiftFilters {
  [key: string]: string | number | boolean | undefined;
  q?: string;
  type?: LiftType;
  maintenanceStatus?: MaintenanceStatus;
  isActive?: boolean;
  sortBy?: 'name' | 'identifier';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

interface CreateLiftData {
  name: string;
  identifier: string;
  type: LiftType;
  capacityKg: number;
  lengthCm: number;
  widthCm: number;
  maxHeightCm: number;
  workshopLocation?: string | null;
  maintenanceStatus?: MaintenanceStatus;
  lastMaintenanceAt?: string | null;
  nextMaintenanceAt?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

interface UpdateLiftData {
  name?: string;
  identifier?: string;
  type?: LiftType;
  capacityKg?: number;
  lengthCm?: number;
  widthCm?: number;
  maxHeightCm?: number;
  workshopLocation?: string | null;
  maintenanceStatus?: MaintenanceStatus;
  lastMaintenanceAt?: string | null;
  nextMaintenanceAt?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

interface UpdateLiftStatusData {
  maintenanceStatus: MaintenanceStatus;
  notes?: string | null;
  lastMaintenanceAt?: string | null;
  nextMaintenanceAt?: string | null;
}

/**
 * Hook to fetch paginated list of lifts with infinite scroll
 */
export function useLifts(filters: LiftFilters = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.lifts.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<Lift>>('/lifts', {
        params: {
          ...filters,
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch all lifts (non-paginated for dropdowns)
 */
export function useAllLifts(activeOnly = true) {
  return useQuery({
    queryKey: queryKeys.lifts.list({ isActive: activeOnly, limit: 50 }),
    queryFn: () =>
      api.get<PaginatedResponse<Lift>>('/lifts', {
        params: { isActive: activeOnly, limit: 50 },
      }),
    staleTime: 60 * 1000,
    select: (data) => data.data,
  });
}

/**
 * Hook to fetch a single lift by ID
 */
export function useLift(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.lifts.detail(id ?? ''),
    queryFn: () => api.get<Lift>(`/lifts/${id}`),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to fetch available lifts for a time range
 */
export function useAvailableLifts(
  startTime: string | undefined,
  endTime: string | undefined,
  vehicleId?: string
) {
  return useQuery({
    queryKey: queryKeys.lifts.available(startTime ?? '', endTime ?? '', vehicleId),
    queryFn: () =>
      api.get<{ data: LiftWithAvailability[] }>('/lifts/available', {
        params: { startTime, endTime, vehicleId },
      }),
    enabled: !!startTime && !!endTime,
    staleTime: 30 * 1000, // 30 seconds - availability can change quickly
    select: (data) => data.data,
  });
}

/**
 * Hook to create a new lift
 */
export function useCreateLift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLiftData) => api.post<Lift>('/lifts', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.lifts.lists(),
      });
    },
  });
}

/**
 * Hook to update an existing lift
 */
export function useUpdateLift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLiftData }) =>
      api.patch<Lift>(`/lifts/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.lifts.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.lifts.lists(),
      });
    },
  });
}

/**
 * Hook to update a lift's maintenance status
 */
export function useUpdateLiftStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLiftStatusData }) =>
      api.patch<Lift>(`/lifts/${id}/status`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.lifts.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.lifts.lists(),
      });
      // Also invalidate availability since status change may affect it
      void queryClient.invalidateQueries({
        queryKey: queryKeys.lifts.all,
      });
    },
  });
}

/**
 * Hook to delete (soft delete) a lift
 */
export function useDeleteLift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/lifts/${id}`),
    onSuccess: () => {
      // Lists, the status dashboard and availability all hide inactive lifts,
      // so a soft delete has to refresh every lift query, not only the lists
      // (#4092).
      void queryClient.invalidateQueries({
        queryKey: queryKeys.lifts.all,
      });
    },
  });
}

/**
 * Hook to batch update lift positions on the floor plan
 */
export function useUpdateLiftPositions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (positions: { id: string; x: number; y: number }[]) =>
      api.patch<{ updated: number }>('/lifts/positions', { positions }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.lifts.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.lifts.all,
      });
    },
  });
}

// Lift status types

export type LiftOccupancy = 'free' | 'in_use' | 'paused' | 'maintenance';

export interface LiftStatusItem {
  id: string;
  name: string;
  identifier: string;
  type: LiftType;
  maintenanceStatus: MaintenanceStatus;
  floorPlanX: number | null;
  floorPlanY: number | null;
  occupancy: LiftOccupancy;
  currentBooking: {
    id: string;
    customerName: string;
    vehicleReg: string;
    vehicleMakeModel: string;
    status: string;
    startedAt: string | null;
    estimatedDurationHours: number | null;
  } | null;
}

export interface LiftUtilizationItem {
  id: string;
  name: string;
  identifier: string;
  totalAvailableMinutes: number;
  totalUsedMinutes: number;
  utilizationPercent: number;
  jobCount: number;
}

export interface LiftUtilizationReport {
  data: LiftUtilizationItem[];
  overall: {
    totalAvailableMinutes: number;
    totalUsedMinutes: number;
    utilizationPercent: number;
  };
  /**
   * The instants the backend resolved. For a `dateFrom`/`dateTo` query,
   * `startDate` is the first instant of `dateFrom` and `endDate` is the
   * EXCLUSIVE end: the first instant of the day after `dateTo`, both in the
   * workshop's time zone.
   */
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

/**
 * Hook to fetch real-time lift status (occupancy)
 */
export function useLiftStatus() {
  return useQuery({
    queryKey: queryKeys.lifts.status(),
    queryFn: () => api.get<{ data: LiftStatusItem[] }>('/lifts/status'),
    staleTime: 15 * 1000, // 15 seconds - real-time data
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds
    select: (data) => data.data,
  });
}

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Hook to fetch the lift utilization report for calendar days (#3859).
 *
 * `dateFrom` / `dateTo` are the report period's `yyyy-MM-dd` labels, both
 * inclusive, sent to the API unchanged. The backend resolves them in the
 * workshop's (tenant) time zone, like every other report, so nothing here
 * depends on the browser's time zone. The query stays disabled until both are
 * well-formed day labels.
 */
export function useLiftUtilization(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: [...queryKeys.lifts.all, 'utilization', { dateFrom, dateTo }] as const,
    queryFn: () =>
      api.get<LiftUtilizationReport>('/lifts/utilization', {
        params: { dateFrom, dateTo },
      }),
    enabled: DATE_KEY.test(dateFrom) && DATE_KEY.test(dateTo),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Lift type / maintenance status labels live in `@/utils/liftLabels` (i18n
// label keys, #4096).

// Helper function to get status color for maintenance status
export function getMaintenanceStatusColor(
  status: MaintenanceStatus
): 'success' | 'warning' | 'error' {
  switch (status) {
    case 'operational':
      return 'success';
    case 'needs_maintenance':
      return 'warning';
    case 'out_of_service':
      return 'error';
    default:
      return 'success';
  }
}
